import { createFileRoute, notFound, Link } from "@tanstack/react-router";
import { ArrowRight } from "lucide-react";

import { cities, services, type CitySlug, serviceLink, cityLink } from "@/lib/site";
import { cityContent } from "@/lib/content";
import { IMAGES } from "@/lib/images";
import { JsonLd } from "@/components/site/JsonLd";
import { localBusinessSchema, cityBusinessSchema, breadcrumbSchema } from "@/lib/schema";
import { FadeUp } from "@/components/site/FadeUp";
import { SectionHeading } from "@/components/site/SectionHeading";
import { LinkButton } from "@/components/site/Buttons";
import { CtaBand } from "@/components/site/CTA";
import { Breadcrumbs } from "@/components/site/Breadcrumbs";
import { InnerHero } from "@/components/site/PageBits";
import { useGoToQuote } from "@/lib/quoteNav";
import { BUSINESS } from "@/lib/site";

export const Route = createFileRoute("/service-areas/$slug")({
  loader: ({ params }) => {
    const slug = params.slug as CitySlug;
    const city = cities.find((c) => c.slug === slug);
    if (!city) throw notFound();
    const content = cityContent[slug];
    if (!content) throw notFound();
    return { city, content };
  },
  head: ({ loaderData }) => {
    const d = loaderData;
    if (!d) return {};
    const title = `${d.city.name} Cleaning Services | Pretty in Pink`;
    const desc = `Residential and commercial cleaning in ${d.city.name}, ${d.city.state} and nearby areas. House cleaning, deep cleaning, move-out & office cleaning. Request a quote.`;
    return {
      meta: [
        { title },
        { name: "description", content: desc },
        { property: "og:title", content: title },
        { property: "og:description", content: desc },
        { property: "og:type", content: "website" },
        { property: "og:image", content: IMAGES.coverBanner },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:image", content: IMAGES.coverBanner },
      ],
    };
  },
  component: CityPage,
});

function CityPage() {
  const goQuote = useGoToQuote();
  const { city, content } = Route.useLoaderData();
  const otherCities = cities.filter((c) => c.slug !== city.slug);

  return (
    <>
      <JsonLd data={localBusinessSchema()} />
      <JsonLd data={cityBusinessSchema(city)} />
      <JsonLd
        data={breadcrumbSchema([
          { name: "Home", path: "/" },
          { name: "Service Areas", path: "/service-areas" },
          { name: `${city.name}, ${city.state}`, path: `/service-areas/${city.slug}` },
        ])}
      />

      <InnerHero
        eyebrow="Service Area"
        title={`Cleaning in ${city.name}, ${city.state}`}
        subtitle={content.intro}
        image={content.heroImage}
        imageAlt={content.heroAlt}
      >
        <div className="mb-4">
          <Breadcrumbs
            items={[
              { name: "Home", path: "/" },
              { name: "Service Areas", path: "/service-areas" },
              { name: `${city.name}, ${city.state}`, path: `/service-areas/${city.slug}` },
            ]}
          />
        </div>
      </InnerHero>

      {/* Local note */}
      <section className="px-4 py-12 md:px-6 md:py-16">
        <FadeUp className="mx-auto max-w-3xl text-center">
          <p className="text-lg text-muted-foreground text-pretty md:text-xl">
            {content.localNote}
          </p>
        </FadeUp>
      </section>

      {/* Services offered here */}
      <section className="bg-secondary/40 px-4 py-12 md:px-6 md:py-16">
        <FadeUp>
          <SectionHeading
            eyebrow="What We Offer"
            title={`Cleaning services in ${city.name}`}
            intro="Every service we offer is available here in your area. Pick one to see what's included."
          />
        </FadeUp>
        <div className="mx-auto mt-10 grid max-w-6xl gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {services.map((s) => (
            <Link
              key={s.slug}
              {...serviceLink(s.slug)}
              className="lift-card group flex flex-col rounded-2xl border border-border bg-card p-6"
            >
              <p className="font-display text-xs font-bold uppercase tracking-wider text-primary">
                {s.short}
              </p>
              <h3 className="mt-1 text-lg font-bold text-ink">{s.name}</h3>
              <p className="mt-2 flex-1 text-sm text-muted-foreground">{s.blurb}</p>
              <span className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-primary">
                Learn more{" "}
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </span>
            </Link>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="px-4 py-12 md:px-6 md:py-16">
        <div className="mx-auto max-w-4xl rounded-3xl border border-border bg-secondary/50 p-8 text-center md:p-12">
          <h2 className="text-2xl font-bold text-ink md:text-3xl text-balance">{content.cta}</h2>
          <div className="mt-6 flex flex-wrap justify-center gap-3">
            <button
              type="button"
              onClick={goQuote}
              className="btn-sparkle inline-flex items-center justify-center bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground"
            >
              <span>Get a Quote</span>
            </button>
            <LinkButton href={BUSINESS.phoneHref} variant="outline">
              Call {BUSINESS.phoneDisplay}
            </LinkButton>
          </div>
        </div>
      </section>

      {/* Other areas */}
      <section className="bg-secondary/40 px-4 py-12 md:px-6 md:py-16">
        <FadeUp>
          <SectionHeading eyebrow="Nearby" title="Other areas we serve" />
        </FadeUp>
        <div className="mx-auto mt-8 flex max-w-4xl flex-wrap justify-center gap-3">
          {otherCities.map((c) => (
            <Link
              key={c.slug}
              {...cityLink(c.slug)}
              className="rounded-full border border-border bg-card px-4 py-2 text-sm font-semibold text-ink transition-colors hover:border-primary hover:text-primary"
            >
              {c.name}, {c.state}
            </Link>
          ))}
        </div>
      </section>

      <CtaBand />
    </>
  );
}
